<script lang="ts" module>
  // ponytail: a textarea-over-highlighted-<pre> editor (the react-simple-code-editor pattern) — proper
  // CSS syntax colours with ZERO deps. The textarea is the real input (caret + editing); the <pre>
  // behind it is cosmetic, scroll-synced. A regex tokenizer is enough for CSS; worst case is a slightly
  // off colour, never broken editing. Upgrade to CodeMirror only if autocomplete/linting is wanted.
  const ESC = (s: string): string => s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]!));
  const TOKEN = /(\/\*[\s\S]*?\*\/|\/\*[\s\S]*$)|("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')|(--[\w-]+)|(#[0-9a-fA-F]{3,8}\b)|(\b[a-zA-Z-]+(?=\s*\())|([{}();:,])|(-?\d*\.?\d+(?:px|deg|rem|em|%|s|ms|vh|vw|fr|ch)?\b)/g;
  export function highlight(css: string): string {
    let out = "", last = 0, m: RegExpExecArray | null;
    TOKEN.lastIndex = 0;
    while ((m = TOKEN.exec(css))) {
      out += ESC(css.slice(last, m.index));
      const cls = m[1] ? "c-com" : m[2] ? "c-str" : m[3] ? "c-var" : m[4] ? "c-hex" : m[5] ? "c-fn" : m[6] ? "c-punct" : "c-num";
      out += `<span class="${cls}">${ESC(m[0])}</span>`;
      last = m.index + m[0].length;
    }
    return out + ESC(css.slice(last)) + "\n"; // trailing newline keeps the last line aligned
  }
</script>

<script lang="ts">
  let { value = "", oninput, textarea = $bindable(null), placeholder = "", class: klass = "" }:
    { value?: string; oninput?: (v: string) => void; textarea?: HTMLTextAreaElement | null; placeholder?: string; class?: string } = $props();

  const html = $derived(highlight(value));
</script>

<div class="cssed {klass}">
  <pre class="cssed-layer cssed-pre" aria-hidden="true">{@html html}</pre>
  <textarea
    bind:this={textarea}
    {value}
    {placeholder}
    oninput={(e) => oninput?.(e.currentTarget.value)}
    spellcheck="false"
    autocomplete="off"
    autocapitalize="off"
    class="cssed-layer cssed-input"
  ></textarea>
</div>

<style>
  /* The editor GROWS with its content (the <pre> is in flow and drives the height); the textarea is
     overlaid exactly on top. Neither scrolls internally — the sidebar scrolls — so there's only ONE
     scrollbar and you can always reach the bottom. */
  .cssed {
    position: relative;
    width: 100%;
    border-radius: 4px;
    background: color-mix(in oklab, var(--color-void) 60%, transparent);
    box-shadow: inset 0 0 0 1px color-mix(in oklab, var(--color-cyan-dim) 20%, transparent);
  }
  .cssed:focus-within {
    box-shadow: inset 0 0 0 1px color-mix(in oklab, var(--color-cyan) 40%, transparent);
  }
  /* the <pre> and <textarea> MUST render identically so the caret lines up with the colours */
  .cssed-layer {
    margin: 0;
    padding: 0.625rem;
    border: 0;
    width: 100%;
    font-family: ui-monospace, "SF Mono", "Cascadia Code", Menlo, Consolas, monospace;
    font-size: 0.7rem;
    line-height: 1.55;
    letter-spacing: 0;
    tab-size: 2;
    white-space: pre-wrap;
    overflow-wrap: break-word;
    word-break: break-word;
    overflow: hidden;
  }
  /* in flow → drives the box height; min-height keeps a usable editor even when near-empty */
  .cssed-pre {
    position: relative;
    min-height: 100%;
    pointer-events: none;
    color: var(--color-ink-dim);
  }
  /* overlaid exactly on the <pre>; transparent text so the highlighted <pre> shows through */
  .cssed-input {
    position: absolute;
    inset: 0;
    color: transparent;
    background: transparent;
    caret-color: var(--color-ink);
    resize: none;
    outline: none;
  }
  .cssed-input::placeholder { color: color-mix(in oklab, var(--color-ink-dim) 50%, transparent); }

  .cssed :global(.c-com) { color: color-mix(in oklab, var(--color-ink-dim) 60%, transparent); font-style: italic; }
  .cssed :global(.c-var) { color: var(--color-cyan); }
  .cssed :global(.c-hex) { color: var(--color-gold); }
  .cssed :global(.c-str) { color: var(--color-lime); }
  .cssed :global(.c-num) { color: var(--color-orange); }
  .cssed :global(.c-fn) { color: var(--color-magenta); }
  .cssed :global(.c-punct) { color: color-mix(in oklab, var(--color-ink-dim) 80%, transparent); }
</style>
